import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  FeatureTourProgress,
  ChecklistItem,
  ChecklistItemKey,
  DEFAULT_CHECKLIST_ITEMS,
  FEATURE_TOUR_STORAGE_KEY,
  CHECKLIST_STORAGE_KEY,
} from '@/types/feature-tour';

interface FeatureTourContextType {
  progress: FeatureTourProgress;
  checklist: ChecklistItem[];
  run: boolean; // Joyride's run prop
  stepIndex: number; // Joyride's stepIndex

  // Tour control
  startTour: () => void;
  stopTour: () => void;
  resetTour: () => void;
  setStepIndex: (index: number) => void;
  completeTour: () => void;

  // Checklist
  completeChecklistItem: (key: ChecklistItemKey) => void;
  isChecklistItemComplete: (key: ChecklistItemKey) => boolean;
  getChecklistProgress: () => { completed: number; total: number; percentage: number };
}

const FeatureTourContext = createContext<FeatureTourContextType | undefined>(undefined);

const initialProgress: FeatureTourProgress = {
  currentStepIndex: 0,
  completedSteps: [],
  isCompleted: false,
};

const initialChecklist: ChecklistItem[] = DEFAULT_CHECKLIST_ITEMS.map(item => ({
  ...item,
  isCompleted: false,
}));

export function FeatureTourProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<FeatureTourProgress>(initialProgress);
  const [checklist, setChecklist] = useState<ChecklistItem[]>(initialChecklist);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Use sessionStorage instead of useRef to persist across component remounts
  // This prevents the tour from appearing multiple times in the same session
  const SESSION_AUTO_START_KEY = 'feature-tour-auto-start-attempted';

  // Load from localStorage on mount
  useEffect(() => {
    const savedProgress = localStorage.getItem(FEATURE_TOUR_STORAGE_KEY);
    const savedChecklist = localStorage.getItem(CHECKLIST_STORAGE_KEY);

    if (savedProgress) {
      try {
        const parsed = JSON.parse(savedProgress);
        setProgress(parsed);
        setStepIndex(parsed.currentStepIndex || 0);
        // Don't auto-resume - user must manually restart from settings
      } catch (error) {
        console.error('Failed to parse feature tour progress:', error);
      }
    }
    // Don't auto-start on first visit - wait for Setup Wizard to complete

    if (savedChecklist) {
      try {
        setChecklist(JSON.parse(savedChecklist));
      } catch (error) {
        console.error('Failed to parse checklist:', error);
      }
    }
  }, []);

  // Save to localStorage whenever progress changes
  useEffect(() => {
    localStorage.setItem(FEATURE_TOUR_STORAGE_KEY, JSON.stringify(progress));
  }, [progress]);

  // Save to localStorage whenever checklist changes
  useEffect(() => {
    localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(checklist));
  }, [checklist]);

  // Listen for Setup Wizard completion to auto-start Feature Tour (first time only)
  useEffect(() => {
    let autoStartTimeoutId: NodeJS.Timeout | null = null;

    const handleWizardComplete = () => {
      const savedProgress = localStorage.getItem(FEATURE_TOUR_STORAGE_KEY);
      const hasAttemptedAutoStart = sessionStorage.getItem(SESSION_AUTO_START_KEY) === 'true';

      console.log('📢 Setup Wizard completed event received:', {
        hasSavedProgress: !!savedProgress,
        hasAttemptedAutoStart,
        currentRun: run
      });

      // Only auto-start if:
      // 1. Tour has never been started before (no saved progress)
      // 2. Haven't attempted auto-start in this session
      // 3. Not already running
      if (!savedProgress && !hasAttemptedAutoStart && !run) {
        console.log('✨ Scheduling Feature Tour auto-start after Setup Wizard...');

        // Mark immediately to prevent multiple schedules
        sessionStorage.setItem(SESSION_AUTO_START_KEY, 'true');

        autoStartTimeoutId = setTimeout(() => {
          console.log('🚀 Auto-starting Feature Tour...');
          setRun(true);
          setProgress(prev => ({
            ...prev,
            startedAt: new Date().toISOString(),
            lastInteractionAt: new Date().toISOString(),
          }));
        }, 1500);
      }
    };

    window.addEventListener('setup-wizard-completed', handleWizardComplete);
    return () => {
      window.removeEventListener('setup-wizard-completed', handleWizardComplete);
      if (autoStartTimeoutId) {
        console.log('🧹 Cleaning up Feature Tour auto-start timeout');
        clearTimeout(autoStartTimeoutId);
      }
    };
  }, [run, SESSION_AUTO_START_KEY]);

  const startTour = () => {
    setRun(true);
    setProgress(prev => ({
      ...prev,
      startedAt: prev.startedAt || new Date().toISOString(),
      lastInteractionAt: new Date().toISOString(),
    }));
  };

  const stopTour = () => {
    setRun(false);
    // Mark as seen/completed so it doesn't auto-start again (user dismissed it)
    // They can still manually restart from Profile/Settings button
    setProgress(prev => ({
      ...prev,
      isCompleted: true,
      lastInteractionAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
    }));
  };

  const resetTour = () => {
    setProgress({
      ...initialProgress,
      startedAt: new Date().toISOString(),
      lastInteractionAt: new Date().toISOString(),
    });
    setChecklist(initialChecklist);
    setStepIndex(0);
    setRun(true);
  };

  const completeTour = () => {
    setProgress(prev => ({
      ...prev,
      isCompleted: true,
      completedAt: new Date().toISOString(),
      completedSteps: [...new Set([...prev.completedSteps, stepIndex])],
    }));
    setRun(false);
    // No event dispatch needed - Feature Tour is the final step
  };

  const completeChecklistItem = (key: ChecklistItemKey) => {
    setChecklist(prev =>
      prev.map(item =>
        item.key === key
          ? { ...item, isCompleted: true, completedAt: new Date().toISOString() }
          : item
      )
    );
  };

  const isChecklistItemComplete = (key: ChecklistItemKey): boolean => {
    return checklist.find(item => item.key === key)?.isCompleted || false;
  };

  const getChecklistProgress = () => {
    const completed = checklist.filter(item => item.isCompleted).length;
    const total = checklist.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  };

  return (
    <FeatureTourContext.Provider
      value={{
        progress,
        checklist,
        run,
        stepIndex,
        startTour,
        stopTour,
        resetTour,
        setStepIndex,
        completeTour,
        completeChecklistItem,
        isChecklistItemComplete,
        getChecklistProgress,
      }}
    >
      {children}
    </FeatureTourContext.Provider>
  );
}

export function useFeatureTour() {
  const context = useContext(FeatureTourContext);
  if (context === undefined) {
    throw new Error('useFeatureTour must be used within a FeatureTourProvider');
  }
  return context;
}
